import { PropsWithChildren, useMemo } from "react";
import {
  MeetingProvider,
  VoiceFocusProvider,
  BackgroundBlurProvider,
  BackgroundReplacementProvider,
  useVoiceFocus,
} from "amazon-chime-sdk-component-library-react";
import {
  AudioInputDevice,
  Device,
  VoiceFocusTransformDevice,
} from "amazon-chime-sdk-js";
import { useAppState } from "../providers/AppStateProvider";
import { meetingConfig } from "../meetingConfig";
import { VideoFiltersCpuUtilization } from "../types";

function MeetingProviderWithDeviceReplacement({ children }: PropsWithChildren) {
  const { addVoiceFocus } = useVoiceFocus();

  const onDeviceReplacement = async (
    nextDevice: string,
    currentDevice: AudioInputDevice
  ): Promise<Device | VoiceFocusTransformDevice> => {
    return currentDevice instanceof VoiceFocusTransformDevice
      ? addVoiceFocus(nextDevice)
      : nextDevice;
  };

  return (
    <MeetingProvider onDeviceReplacement={onDeviceReplacement}>
      {children}
    </MeetingProvider>
  );
}

export function ChimeFeatureWrappers({ children }: PropsWithChildren) {
  const {
    isWebAudioEnabled,
    videoTransformCpuUtilization,
    imageBlob,
    joinInfo,
  } = useAppState();
  const logger = meetingConfig.logger;

  const filterEnabled =
    videoTransformCpuUtilization !== VideoFiltersCpuUtilization.Disabled;
  const filterCPU = parseInt(videoTransformCpuUtilization, 10) || 40;

  const voiceFocusSpec = useMemo(() => {
    const echoReduction =
      joinInfo?.Meeting?.MeetingFeatures?.Audio?.EchoReduction;
    return echoReduction === "AVAILABLE" ? "ns_es" : "default";
  }, [joinInfo]);

  let wrapped = children;

  if (filterEnabled) {
    wrapped = (
      <BackgroundBlurProvider
        options={{ filterCPUUtilization: filterCPU, logger }}
      >
        <BackgroundReplacementProvider
          options={{ imageBlob, filterCPUUtilization: filterCPU, logger }}
        >
          {wrapped}
        </BackgroundReplacementProvider>
      </BackgroundBlurProvider>
    );
  }

  if (isWebAudioEnabled) {
    wrapped = (
      <VoiceFocusProvider
        spec={{ name: voiceFocusSpec }}
        createMeetingResponse={joinInfo}
      >
        <MeetingProviderWithDeviceReplacement>
          {wrapped}
        </MeetingProviderWithDeviceReplacement>
      </VoiceFocusProvider>
    );
  } else {
    wrapped = <MeetingProvider>{wrapped}</MeetingProvider>;
  }

  return <>{wrapped}</>;
}
